import { useCallback } from 'react';
import type { BugReportSubmission } from '@sorrem/shared';
import { isApiFailure, resolveApiErrorMessage, useSubmitBugReportMutation } from '@sorrem-client/ui/store';
import { useMutationError } from './use-mutation-error';

/** What to tell a tester whose report failed: the rate limit has its own words. */
function describeBugReportFailure(failure: unknown): string {
    return isApiFailure(failure) && failure.data.code === 'rate-limited'
        ? 'You have sent several reports just now. Wait a few minutes, or tell us on Discord.'
        : resolveApiErrorMessage(failure, 'The report could not be sent. Try again, or tell us on Discord.');
}

export interface BugReportState {
    isSending: boolean;
    /** Why the last report failed, or null. */
    error: string | null;
    /** Sends a report. Resolves to its id, or null when it failed. */
    submit: (report: BugReportSubmission) => Promise<string | null>;
}

/** Sending a tester's bug report (#218). */
export function useBugReport(): BugReportState {
    const [submitReport, { isLoading: isSending }] = useSubmitBugReportMutation();
    const { error, run } = useMutationError();

    const submit = useCallback(async (report: BugReportSubmission): Promise<string | null> => {
        let reportId: string | null = null;
        await run(async () => {
            reportId = (await submitReport(report).unwrap()).reportId;
        }, describeBugReportFailure);
        return reportId;
    }, [run, submitReport]);

    return { isSending, error, submit };
}
